export type FieldType = 'text' | 'textarea' | 'date' | 'number' | 'url' | 'select';

export interface CrudField {
  name: string;
  label: string;
  type?: FieldType;
  placeholder?: string;
  required?: boolean;
  helpText?: string;
  /**
   * Solo para `type: 'select'`. Endpoint del que cargar las opciones y campos
   * de cada elemento que hacen de valor y de etiqueta.
   */
  options?: {
    endpoint: string;
    valueField: string;
    labelField: string;
    /** Texto de la opción vacía. Si se omite, el campo es obligatorio de facto. */
    emptyLabel?: string;
  };
}

export interface CrudConfig {
  ctype: string;              // backend POST/PUT/DELETE path: /admin/{ctype}
  listEndpoint: string;       // GET endpoint to list items (without /admin)
  title: string;
  singular: string;
  fields: CrudField[];
  /**
   * Campo que identifica al elemento en las rutas PUT/DELETE `/admin/{ctype}/{clave}`.
   * Por defecto `id`. El evangelio y la oración del día se identifican por `date`,
   * que es lo que esperan `admin_update_content` y `admin_delete_content`.
   */
  keyField?: string;
  /** Optional defaults applied on create (not edit). */
  createDefaults?: Record<string, unknown>;
  /** Optional transform from form values to payload. */
  toPayload?: (values: Record<string, string>) => Record<string, unknown>;
  /** Map a backend item to form values for editing. */
  toFormValues: (item: Record<string, unknown>) => Record<string, string>;
  /** If true, the list shows up/down buttons that call POST /admin/{ctype}/{id}/move. */
  reorderable?: boolean;
}

const stringify = (v: unknown) => (v === undefined || v === null ? '' : String(v));

const numberOrUndef = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) && v.trim() !== '' ? n : undefined;
};

export const CRUD_CONFIGS: Record<string, CrudConfig> = {
  events: {
    ctype: 'events',
    listEndpoint: '/events',
    title: 'Eventos',
    singular: 'evento',
    fields: [
      { name: 'title', label: 'Título', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      { name: 'date', label: 'Fecha (dd/mm/aaaa)', type: 'date', placeholder: 'dd/mm/aaaa' },
      { name: 'location', label: 'Ubicación' },
      { name: 'type', label: 'Tipo (taller/retiro/jornada)' },
      { name: 'image_url', label: 'URL de imagen', type: 'url' },
      { name: 'link', label: 'Link web', type: 'url' },
    ],
    toFormValues: (item) => ({
      title: stringify(item.title),
      description: stringify(item.description),
      date: stringify(item.date),
      location: stringify(item.location),
      type: stringify(item.type),
      image_url: stringify(item.image_url),
      link: stringify(item.link),
    }),
    toPayload: (v) => ({
      title: v.title,
      description: v.description,
      date: v.date,
      location: v.location,
      type: v.type,
      image_url: v.image_url,
      link: v.link || 'https://evangelizaciondigitalhuelva.org/eventos',
    }),
  },
  prayers: {
    ctype: 'prayers',
    listEndpoint: '/prayers',
    title: 'Oraciones',
    singular: 'oración',
    reorderable: true,
    fields: [
      { name: 'title', label: 'Título', required: true },
      { name: 'text', label: 'Texto de la oración', type: 'textarea', required: true },
      { name: 'category', label: 'Categoría (mañana/noche/maría/jóvenes)' },
    ],
    toFormValues: (item) => ({
      title: stringify(item.title),
      text: stringify(item.text),
      category: stringify(item.category),
    }),
    toPayload: (v) => ({
      title: v.title,
      text: v.text,
      category: v.category,
      is_featured: false,
    }),
  },
  resources: {
    ctype: 'resources',
    listEndpoint: '/resources',
    title: 'Recursos',
    singular: 'recurso',
    fields: [
      { name: 'title', label: 'Título', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      { name: 'file_url', label: 'URL del archivo (PDF/link)', type: 'url' },
      { name: 'image_url', label: 'URL de imagen', type: 'url' },
      { name: 'category', label: 'Categoría (guía/plantilla/reflexión)' },
    ],
    toFormValues: (item) => ({
      title: stringify(item.title),
      description: stringify(item.description),
      file_url: stringify(item.file_url),
      image_url: stringify(item.image_url),
      category: stringify(item.category),
    }),
    toPayload: (v) => ({
      title: v.title,
      description: v.description,
      file_url: v.file_url,
      image_url: v.image_url,
      category: v.category || 'guía',
    }),
  },
  news: {
    ctype: 'news',
    listEndpoint: '/news',
    title: 'Noticias',
    singular: 'noticia',
    fields: [
      { name: 'title', label: 'Título', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      { name: 'content', label: 'Contenido completo', type: 'textarea' },
      { name: 'image_url', label: 'URL de imagen', type: 'url' },
      { name: 'url', label: 'URL del artículo', type: 'url' },
    ],
    toFormValues: (item) => ({
      title: stringify(item.title),
      description: stringify(item.description),
      content: stringify(item.content),
      image_url: stringify(item.image_url),
      url: stringify(item.url),
    }),
    toPayload: (v) => ({
      title: v.title,
      description: v.description,
      content: v.content,
      image_url: v.image_url,
      url: v.url,
      type: 'noticia',
      published_at: new Date().toISOString(),
    }),
    // La publicación cruzada a la web (antes PUT /articles/sync/{id}, un
    // endpoint que nunca existió en el backend web) se ha retirado de aquí.
    // Queda pendiente de un endpoint real con mapeo de campos explícito
    // (ver "Fase 3" en el estudio de fusión de paneles de administración,
    // repo evangelizacion-digital-huelva).
  },
  courses: {
    ctype: 'courses',
    listEndpoint: '/courses',
    title: 'Formación',
    singular: 'curso',
    fields: [
      { name: 'title', label: 'Título', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      { name: 'image_url', label: 'URL de imagen', type: 'url' },
      { name: 'level', label: 'Nivel (básico/medio/avanzado)' },
      { name: 'duration', label: 'Duración (ej: 8 semanas)' },
      { name: 'modules_count', label: 'Nº de módulos', type: 'number' },
      { name: 'audience', label: 'Audiencia' },
      { name: 'date', label: 'Fecha inicio (ej: 15 jun 2025)' },
      { name: 'inscription_url', label: 'URL de inscripción', type: 'url' },
    ],
    toFormValues: (item) => ({
      title: stringify(item.title),
      description: stringify(item.description),
      image_url: stringify(item.image_url),
      level: stringify(item.level),
      duration: stringify(item.duration),
      modules_count: stringify(item.modules_count),
      audience: stringify(item.audience),
      date: stringify(item.date),
      inscription_url: stringify(item.inscription_url),
    }),
    toPayload: (v) => ({
      title: v.title,
      description: v.description,
      image_url: v.image_url || undefined,
      level: v.level || undefined,
      duration: v.duration || undefined,
      modules_count: numberOrUndef(v.modules_count),
      audience: v.audience || undefined,
      date: v.date || undefined,
      inscription_url: v.inscription_url || undefined,
    }),
  },
  podcast: {
    ctype: 'podcast',
    listEndpoint: '/podcast',
    title: 'Podcast',
    singular: 'episodio',
    fields: [
      { name: 'title', label: 'Título', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      { name: 'audio_url', label: 'URL del audio', type: 'url', required: true },
      { name: 'image_url', label: 'URL de imagen (portada)', type: 'url' },
      {
        name: 'program_id',
        label: 'Programa',
        type: 'select',
        options: { endpoint: '/podcast/programs', valueField: 'id', labelField: 'title' },
        helpText: 'Los episodios sin programa se agrupan aparte en la app.',
      },
      { name: 'episode', label: 'Episodio nº', type: 'number' },
      { name: 'duration', label: 'Duración (ej: 12:34)' },
      { name: 'category', label: 'Categoría' },
    ],
    toFormValues: (item) => ({
      title: stringify(item.title),
      description: stringify(item.description),
      audio_url: stringify(item.audio_url),
      image_url: stringify(item.image_url),
      program_id: stringify(item.program_id),
      episode: stringify(item.episode),
      duration: stringify(item.duration),
      category: stringify(item.category),
    }),
    toPayload: (v) => ({
      title: v.title,
      description: v.description,
      audio_url: v.audio_url,
      image_url: v.image_url || undefined,
      program_id: v.program_id || '',
      episode: numberOrUndef(v.episode) ?? 1,
      duration: v.duration || undefined,
      category: v.category || undefined,
      published_at: new Date().toISOString(),
    }),
  },
  meditations: {
    ctype: 'meditations',
    listEndpoint: '/meditations',
    title: 'Meditaciones',
    singular: 'meditación',
    fields: [
      { name: 'title', label: 'Título', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      { name: 'audio_url', label: 'URL del audio', type: 'url', required: true },
      { name: 'image_url', label: 'URL de imagen (portada)', type: 'url' },
      { name: 'duration', label: 'Duración (ej: 8:00)' },
      { name: 'category', label: 'Categoría (adviento/cuaresma/diaria/etc)' },
    ],
    toFormValues: (item) => ({
      title: stringify(item.title),
      description: stringify(item.description),
      audio_url: stringify(item.audio_url),
      image_url: stringify(item.image_url),
      duration: stringify(item.duration),
      category: stringify(item.category),
    }),
    toPayload: (v) => ({
      title: v.title,
      description: v.description,
      audio_url: v.audio_url,
      image_url: v.image_url || undefined,
      duration: v.duration || undefined,
      category: v.category || undefined,
      published_at: new Date().toISOString(),
    }),
  },
  'podcast-programs': {
    ctype: 'podcast-programs',
    listEndpoint: '/podcast/programs',
    title: 'Programas de podcast',
    singular: 'programa',
    fields: [
      { name: 'title', label: 'Título del programa', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      { name: 'image_url', label: 'URL de imagen (portada)', type: 'url' },
      { name: 'order', label: 'Orden', type: 'number', helpText: 'Menor número, más arriba en la app.' },
    ],
    toFormValues: (item) => ({
      title: stringify(item.title),
      description: stringify(item.description),
      image_url: stringify(item.image_url),
      order: stringify(item.order),
    }),
    toPayload: (v) => ({
      title: v.title,
      description: v.description,
      image_url: v.image_url || undefined,
      order: numberOrUndef(v.order) ?? 0,
      active: true,
    }),
  },
  // Archivo de evangelios ya publicados. Comparte `ctype` con la página de
  // "Evangelio del día" (que solo publica); esta añade listar, corregir y borrar.
  // Se identifica por fecha, no por id: es la clave lógica de daily_gospel.
  'gospel-archivo': {
    ctype: 'gospel',
    listEndpoint: '/gospel/history',
    title: 'Archivo de evangelios',
    singular: 'evangelio',
    keyField: 'date',
    fields: [
      { name: 'date', label: 'Fecha (dd/mm/aaaa)', type: 'date', placeholder: 'dd/mm/aaaa', required: true },
      { name: 'reference', label: 'Referencia (ej: Mc 16, 15)', required: true },
      { name: 'text', label: 'Texto del Evangelio', type: 'textarea' },
      { name: 'evangelio_lectura', label: 'Lectura completa del Evangelio', type: 'textarea' },
      { name: 'reflection', label: 'Reflexión', type: 'textarea' },
      {
        name: 'image_url',
        label: 'URL de imagen',
        type: 'url',
        helpText: 'Enlace directo a la imagen (termina en .jpg, .png o .webp). Si no se ve aquí la miniatura, tampoco se verá en la app.',
      },
    ],
    toFormValues: (item) => ({
      date: stringify(item.date),
      reference: stringify(item.reference),
      text: stringify(item.text),
      evangelio_lectura: stringify(item.evangelio_lectura),
      reflection: stringify(item.reflection),
      image_url: stringify(item.image_url),
    }),
    toPayload: (v) => ({
      date: v.date,
      reference: v.reference,
      text: v.text,
      evangelio_lectura: v.evangelio_lectura,
      reflection: v.reflection,
      image_url: v.image_url,
      author: 'Delegación de Evangelización Digital',
    }),
  },
};
